import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, startWith } from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import AppState from '../reducers/app-state';
import { ID, ReduxAction } from '@repo/utils/types';
import { activityApiObservable } from '@repo/utils/helpers/api';
import { ActivityItem, ActivityItemType } from '@repo/utils/models/Activity';
import { selectActiveActivityFilter } from '../reducers/data/activity';

/* Epic actions */
const activityActions = requestActions('ACTIVITY', ['fetchAll', 'update']);
const activityItemActions = requestActions('ACTIVITY_ITEM', ['delete', 'update']);
const activityFilters = requestActions('ACTIVITY_FILTER', ['fetchAll']);
const reconnectActivityItemPersonActions = requestActions('RECONNECT_ACTIVITY_ITEM_PERSON', [
  'update',
]);

const activityNotificationActions = requestActions('ACTIVITY_NOTIFICATION', ['fetchAll']);

/* Component actions */
// Always fetches the first page and overwrites whatever is in state
export const fetchActivity = activityActions['fetchAll'];
// Handles pagination. Subsequent pages are merged into the already loaded feed.
export const fetchMoreActivity = () => ({ type: 'FETCH_MORE_ACTIVITY' });

export const fetchActivityFilters = activityFilters['fetchAll'];

export const fetchActivityNotifications = activityNotificationActions['fetchAll'];
export const fetchMoreActivityNotifications = () => ({
  type: 'FETCH_MORE_ACTIVITY_NOTIFICATIONS',
});

export const markAllActivityNotificationsAsSeen = () => ({
  type: 'MARK_ACTIVITY_NOTIFICATIONS_AS_SEEN',
  payload: { all: true },
});

export const markActivityNotificationsAsSeen = (itemIDs: ID[]) => ({
  type: 'MARK_ACTIVITY_NOTIFICATIONS_AS_SEEN',
  payload: { itemIDs },
});

export const setActivityFilter = (filter: ActivityItemType) => ({
  type: 'SET_ACTIVITY_FILTER',
  payload: { filter },
});

export const dismissActivityItem = activityItemActions['delete'];

// Operates on whatever the current active filter is
export const dismissAllActivityItems = ({ alertOnSuccess }: { alertOnSuccess?: string }) =>
  activityActions['update']({ status: 'dismissed', alertOnSuccess });
// Currently will be relegated to use in staging
export const restoreAllActivityItems = () =>
  activityActions['update']({ status: 'active', updateAll: true });

export const markActivityAsSeen = () => ({ type: 'MARK_ACTIVITY_AS_SEEN' });

export const dismissPersonFromReconnect = (reconnectActivityItem: ActivityItem, contactID: ID) => {
  if (
    reconnectActivityItem.filteredContacts.length === 1 &&
    reconnectActivityItem.contactStatus(contactID) === 'active'
  ) {
    return dismissActivityItem({ id: reconnectActivityItem.id });
  }

  return reconnectActivityItemPersonActions['update']({
    activityItemID: reconnectActivityItem.id,
    contactID,
    status: 'dismissed',
  });
};

/* Observables/Epic helpers */
const fetchActivityObservable = (limit: number, filter?: string, after?: string) => {
  let query: { limit: number; after?: string; type?: string } = { limit };

  if (filter) {
    query = { ...query, type: filter };
  }

  if (after) {
    query = { ...query, after };
  }

  return activityApiObservable('GET', 'activity/', {
    query: { ...query, exclude_notifications: 'true' },
  }).pipe(
    map((response) =>
      activityActions['fetchAllSucceeded']({ ...response, filter, resultsAfterDate: after }),
    ),
    startWith(activityActions['fetchAllRequested']({ filter })),
    catchError((err) => of(activityActions['fetchAllFailed'](err))),
  );
};

/* Epics */
const fetchActivityEpic = (action$: Observable<ReduxAction>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(
      'FETCH_ALL_ACTIVITY',
      'SET_ACTIVITY_FILTER',
      // 'CREATE_CONTACT_SUCCEEDED',
      // 'DELETE_CONTACT_SUCCEEDED',
      'MERGE_CONTACT_BATCH_SUCCEEDED',
    ),
    mergeMap(({ payload }) =>
      fetchActivityObservable(
        state$.value.data.activity.all.limit,
        payload?.filter || state$.value.data.activity.activeFilter,
      ),
    ),
  );

const fetchMoreActivityEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_MORE_ACTIVITY'),
    mergeMap(() =>
      fetchActivityObservable(
        state$.value.data.activity.all.limit,
        state$.value.data.activity.activeFilter,
        !!state$.value.data.activity.activeFilter
          ? state$.value.data.activity.filtered.oldestItemDate
          : state$.value.data.activity.all.oldestItemDate,
      ),
    ),
  );

const fetchActivityNotificationsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_ALL_ACTIVITY_NOTIFICATIONS'),
    mergeMap(() =>
      activityApiObservable('GET', 'notifications/', { query: { limit: 30 } }).pipe(
        map((response) => activityNotificationActions['fetchAllSucceeded']({ ...response })),
        startWith(activityNotificationActions['fetchAllRequested']()),
        catchError((err) => of(activityNotificationActions['fetchAllFailed'](err))),
      ),
    ),
  );

const fetchMoreActivityNotificationsEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_MORE_ACTIVITY_NOTIFICATIONS'),
    mergeMap(() => {
      const after = state$.value.data.activityNotifications.all.oldestItemDate;
      return activityApiObservable('GET', 'notifications/', { query: { limit: 30, after } }).pipe(
        map((response) =>
          activityNotificationActions['fetchAllSucceeded']({
            ...response,
            resultsAfterDate: after,
          }),
        ),
        startWith(activityNotificationActions['fetchAllRequested']()),
        catchError((err) => of(activityNotificationActions['fetchAllFailed'](err))),
      );
    }),
  );

const fetchActivityFiltersEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType(
      'FETCH_ALL_ACTIVITY_FILTERS',
      // Ideally we get these from the server, but they don't update immediately. So we'll track locally
      // for now and overwrite whenever we refetch on viewing Activity.
      // 'UPDATE_ACTIVITY_SUCCEEDED',
      // 'DELETE_ACTIVITY_ITEM_SUCCEEDED',
    ),
    mergeMap(() =>
      activityApiObservable('GET', 'activity/types/').pipe(
        map((response) => activityFilters['fetchAllSucceeded'](response)),
        startWith(activityFilters['fetchAllRequested']()),
        catchError((err) => of(activityFilters['fetchAllFailed'](err))),
      ),
    ),
  );

export const deleteActivityItemEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('DELETE_ACTIVITY_ITEM'),
    mergeMap(({ payload }) => {
      const activeFilter = selectActiveActivityFilter(state$.value);
      const collectionKey = activeFilter ? 'filtered' : 'all';
      const initialItemsState = state$.value.data.activity[collectionKey].items;

      let itemType = activeFilter;
      if (!itemType) {
        itemType = state$.value.data.activity.all.items.get(payload.id)?.type;
      }

      return activityApiObservable('PATCH', `activity/${payload.id}/`, {
        body: { status: 'dismissed' },
      }).pipe(
        map(() => activityItemActions['deleteSucceeded']({ ...payload, itemType })),
        startWith(activityItemActions['deleteRequested']({ filter: activeFilter, id: payload.id })),
        catchError((error) =>
          of(
            activityItemActions['deleteFailed']({
              ...payload,
              error,
              initialItemsState,
              filter: activeFilter,
            }),
          ),
        ),
      );
    }),
  );

// This ensures that we drop the dismissed item after 150ms whether the request has completed or not
const clearActivityItemFromStoreEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('DELETE_ACTIVITY_ITEM_REQUESTED'),
    delay(150),
    map(({ payload }) => ({ type: 'CLEAR_ACTIVITY_ITEM_FROM_STORE', payload })),
  );

export const updateActivityEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('UPDATE_ACTIVITY'),
    mergeMap(({ payload }) => {
      let activeFilter = selectActiveActivityFilter(state$.value);

      if (payload.updateAll) {
        activeFilter = undefined;
      }

      const initialCollectionsState = {
        all: state$.value.data.activity.all,
        filtered: state$.value.data.activity.filtered,
      };

      return activityApiObservable('PATCH', `activity/`, {
        body: { status: payload.status, type: activeFilter },
      }).pipe(
        map(() =>
          activityActions['updateSucceeded']({
            ...payload,
            type: activeFilter,
            initialCollectionsState,
          }),
        ),
        startWith(activityActions['updateRequested']({ ...payload, type: activeFilter })),
        catchError((error) =>
          of(activityItemActions['updateFailed']({ ...payload, error, initialCollectionsState })),
        ),
      );
    }),
  );

// This only handles the API dispatch. The UI state update is done after a 5s delay below
const markActivityAsSeenEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('MARK_ACTIVITY_AS_SEEN'),
    mergeMap(() =>
      activityApiObservable('PATCH', `activity/`, {
        body: { seen: true, channel: 'activity' },
      }).pipe(
        map(() => ({ type: 'MARK_ACTIVITY_AS_SEEN_SUCCEEDED', payload: {} })),
        catchError(() => of({ type: 'MARK_ACTIVITY_AS_SEEN_FAILED', payload: {} })),
      ),
    ),
  );

const markActivityNotificationsAsSeenEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('MARK_ACTIVITY_NOTIFICATIONS_AS_SEEN'),
    mergeMap(({ payload }) => {
      const itemIDs = payload.itemIDs ? { ids: payload.itemIDs } : {};

      return activityApiObservable('PATCH', 'notifications/', {
        body: { seen: true, ...itemIDs },
      }).pipe(
        map(() => ({ type: 'MARK_ACTIVITY_NOTIFICATIONS_AS_SEEN_SUCCEEDED', payload })),
        catchError(() => of({ type: 'MARK_ACTIVITY_NOTIFICATIONS_AS_SEEN_FAILED', payload })),
      );
    }),
  );

// This clears the unseen activity state in the UI after 5s. Regardless of whether the API call
// succeeded or not
const clearUnseenActivityEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('MARK_ACTIVITY_AS_SEEN_SUCCEEDED', 'MARK_ACTIVITY_AS_SEEN_FAILED'),
    delay(8000),
    map(() => ({ type: 'CLEAR_UNSEEN_ACTIVITY', payload: {} })),
  );

const alertActivityItemsDeleteSuccessEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_ACTIVITY_SUCCEEDED'),
    filter(({ payload }) => !!payload.alertOnSuccess),
    map(({ payload }) => ({
      type: 'ALERT_USER',
      payload: {
        type: 'success',
        text: payload.alertOnSuccess,
      },
    })),
  );

const updateActivityItemEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('UPDATE_RECONNECT_ACTIVITY_ITEM_PERSON'),
    mergeMap(({ payload }) => {
      const { activityItemID, contactID, status } = payload;
      return activityApiObservable('PATCH', `activity/${activityItemID}/contacts/${contactID}`, {
        body: { status },
      }).pipe(
        map(() => reconnectActivityItemPersonActions['updateSucceeded'](payload)),
        startWith(reconnectActivityItemPersonActions['updateRequested'](payload)),
        catchError((error) =>
          of(reconnectActivityItemPersonActions['updateFailed']({ ...payload, error })),
        ),
      );
    }),
  );

export const activityEpic = combineEpics(
  fetchActivityEpic,
  fetchMoreActivityEpic,
  fetchActivityFiltersEpic,
  fetchActivityNotificationsEpic,
  fetchMoreActivityNotificationsEpic,
  deleteActivityItemEpic,
  clearActivityItemFromStoreEpic,
  updateActivityEpic,
  markActivityAsSeenEpic,
  markActivityNotificationsAsSeenEpic,
  clearUnseenActivityEpic,
  alertActivityItemsDeleteSuccessEpic,
  updateActivityItemEpic,
);
